import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IContact } from "../models/Contact";
import { IEbayOffer } from "../models/EbayOffer";

export default function useOffers() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const upsertOfferInRow = (row: any, incomingOffer: any) => {
    if (!row) return row;
    const existingOffers = Array.isArray(row.offers) ? row.offers : [];
    const existingIdx = existingOffers.findIndex((x: any) => x?.id === incomingOffer?.id);

    if (existingIdx >= 0) {
      const nextOffers = [...existingOffers];
      nextOffers[existingIdx] = { ...nextOffers[existingIdx], ...incomingOffer };
      return { ...row, offers: nextOffers };
    }

    return { ...row, offers: [incomingOffer, ...existingOffers] };
  };

  const create = useMutation(
    (offer: IEbayOffer) => post(`/part/offers`, offer),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  //Optional body to patch in things after the defaults
  const createDefaultOffer = useMutation(
    ({ partId, body }: { partId: string; body: any }) => post(`/part/offers/default/${partId}`, body),
    {
      onMutate: async ({ partId, body }: { partId: string; body: any }) => {
        await queryClient.cancelQueries(["pagedTable"]);
        await queryClient.cancelQueries(["parts", partId]);
        await queryClient.cancelQueries(["partDetails", partId]);

        const previousPagedQueries = queryClient.getQueriesData(["pagedTable"]);
        const previousPart = queryClient.getQueryData(["parts", partId]);
        const previousPartDetails = queryClient.getQueryData(["partDetails", partId]);

        const optimisticOffer = {
          id: `optimistic-offer-${partId}`,
          partDataId: partId,
          ...body,
        };

        previousPagedQueries.forEach(([queryKey]) => {
          queryClient.setQueryData(queryKey, (currentData: any) => {
            if (!currentData?.data?.rows) return currentData;
            return {
              ...currentData,
              data: {
                ...currentData.data,
                rows: currentData.data.rows.map((row: any) =>
                  row.id === partId ? upsertOfferInRow(row, optimisticOffer) : row
                ),
              },
            };
          });
        });

        queryClient.setQueryData(["parts", partId], (currentData: any) => {
          if (!currentData?.data) return currentData;
          return {
            ...currentData,
            data: upsertOfferInRow(currentData.data, optimisticOffer),
          };
        });

        queryClient.setQueryData(["partDetails", partId], (currentData: any) => {
          if (!currentData?.data) return currentData;
          return {
            ...currentData,
            data: upsertOfferInRow(currentData.data, optimisticOffer),
          };
        });

        return { previousPagedQueries, previousPart, previousPartDetails, partId };
      },
      onError: (_err, variables, context: any) => {
        context?.previousPagedQueries?.forEach(([queryKey, oldData]: any) => {
          queryClient.setQueryData(queryKey, oldData);
        });

        if (context?.previousPart !== undefined) {
          queryClient.setQueryData(["parts", variables.partId], context.previousPart);
        }
        if (context?.previousPartDetails !== undefined) {
          queryClient.setQueryData(["partDetails", variables.partId], context.previousPartDetails);
        }
      },
      onSuccess: (response, variables) => {
        const createdOffer = response?.data?.offers?.[0];
        if (createdOffer) {
          const partId = variables.partId;

          const pagedQueries = queryClient.getQueriesData(["pagedTable"]);
          pagedQueries.forEach(([queryKey]) => {
            queryClient.setQueryData(queryKey, (currentData: any) => {
              if (!currentData?.data?.rows) return currentData;
              return {
                ...currentData,
                data: {
                  ...currentData.data,
                  rows: currentData.data.rows.map((row: any) =>
                    row.id === partId ? upsertOfferInRow(row, createdOffer) : row
                  ),
                },
              };
            });
          });

          queryClient.setQueryData(["parts", partId], (currentData: any) => {
            if (!currentData?.data) return currentData;
            return {
              ...currentData,
              data: upsertOfferInRow(currentData.data, createdOffer),
            };
          });

          queryClient.setQueryData(["partDetails", partId], (currentData: any) => {
            if (!currentData?.data) return currentData;
            return {
              ...currentData,
              data: upsertOfferInRow(currentData.data, createdOffer),
            };
          });
        }

        queryClient.invalidateQueries(["parts", "multiple"]);
        queryClient.invalidateQueries(["partDetails", variables.partId]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const update = useMutation(
    (offer: IEbayOffer) => put(`/part/offers/${offer.id}`, offer),
    {
      onMutate: async (offer: IEbayOffer) => {
        await queryClient.cancelQueries(["pagedTable"]);
        if (offer.partDataId) {
          await queryClient.cancelQueries(["parts", offer.partDataId]);
          await queryClient.cancelQueries(["partDetails", offer.partDataId]);
        }

        const previousPagedQueries = queryClient.getQueriesData(["pagedTable"]);
        const previousPart = offer.partDataId
          ? queryClient.getQueryData(["parts", offer.partDataId])
          : undefined;
        const previousPartDetails = offer.partDataId
          ? queryClient.getQueryData(["partDetails", offer.partDataId])
          : undefined;

        previousPagedQueries.forEach(([queryKey]) => {
          queryClient.setQueryData(queryKey, (currentData: any) => {
            if (!currentData?.data?.rows) return currentData;
            return {
              ...currentData,
              data: {
                ...currentData.data,
                rows: currentData.data.rows.map((row: any) =>
                  row.id === offer.partDataId ? upsertOfferInRow(row, offer) : row
                ),
              },
            };
          });
        });

        if (offer.partDataId) {
          queryClient.setQueryData(["parts", offer.partDataId], (currentData: any) => {
            if (!currentData?.data) return currentData;
            return {
              ...currentData,
              data: upsertOfferInRow(currentData.data, offer),
            };
          });

          queryClient.setQueryData(["partDetails", offer.partDataId], (currentData: any) => {
            if (!currentData?.data) return currentData;
            return {
              ...currentData,
              data: upsertOfferInRow(currentData.data, offer),
            };
          });
        }

        return { previousPagedQueries, previousPart, previousPartDetails };
      },
      onError: (_err, offer, context: any) => {
        context?.previousPagedQueries?.forEach(([queryKey, oldData]: any) => {
          queryClient.setQueryData(queryKey, oldData);
        });

        if (offer.partDataId && context?.previousPart !== undefined) {
          queryClient.setQueryData(["parts", offer.partDataId], context.previousPart);
        }
        if (offer.partDataId && context?.previousPartDetails !== undefined) {
          queryClient.setQueryData(["partDetails", offer.partDataId], context.previousPartDetails);
        }
      },
      onSuccess: (_, offer) => {
        queryClient.invalidateQueries(["pagedTable"]);
        queryClient.invalidateQueries(["parts", offer.partDataId]);
        queryClient.invalidateQueries(["partDetails", offer.partDataId]);
        queryClient.invalidateQueries(["basket"]);
      },
    }
  );

  return {
    create,
    createDefaultOffer,
    update,
  };
}
